import { Alert, Card, CardActionArea, CardContent, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import { fetchMonitor, monitorQueryKey } from "v2/pages/Workspace/Activity/AppCampaign/Monitor/monitor.service";
import { MonitorableType } from "v2/pages/Workspace/Activity/AppCampaign/Monitor/monitor.types";
import { CAMPAIGN_TYPE_LABEL, CampaignTypeStateValue } from "v2/services/contractLifecycle.service";

interface Props {
  workspaceId: string;
  templateId: string;
  campaignWorkspaceActivityId: string | null;
  /** invariant: estado do tipo lido de `GET /lifecycle`; nulo (ciclo de vida não lido) não consulta, como OFF. */
  typeState: CampaignTypeStateValue | null;
}

const TYPE: MonitorableType = "contributario";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * why: spec 29/09 §5.1, o passo inicial de Conferir mostra quantos titulares a campanha segura e abre o monitor.
 * hazard: compor o monitor acorda o warehouse do Snowflake (60 s faturados); por isso só consulta com o tipo
 * fora de OFF (R14), não acompanha a reconsulta de 5 s de Conferir e não refaz ao voltar com o cache fresco.
 * invariant: sem `refetchOnMount: false`; o `staleTime` segura o custo e o cache invalidado por uma ação
 * do monitor (inclusive o do CNPJ) tem de refazer ao montar, senão o cartão mostra a fila de antes.
 */
export function ContributarioCard({ workspaceId, templateId, campaignWorkspaceActivityId, typeState }: Props) {
  const history = useHistory();
  const source = { kind: "WORKSPACE" as const, workspaceId };
  const enabled = typeState !== null && typeState !== "OFF";
  const query = useQuery({
    queryKey: monitorQueryKey(source, TYPE),
    queryFn: () => fetchMonitor(source, TYPE),
    enabled,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const label = CAMPAIGN_TYPE_LABEL[TYPE];
  if (!enabled || query.isPending) return null;
  if (query.isError) return <Alert severity="warning">{`${label}: não foi possível ler a campanha.`}</Alert>;

  const { top, contracts } = query.data;
  const unread = contracts.filter((c) => c.unreadable);
  // invariant: contrato não lido nunca vira "0 titulares aguardando" (obrigação F5 da T8).
  const text = unread.length
    ? `${label}: dados do contrato não lidos (${unread.map((c) => c.unreadable).join("; ")})`
    : `${label}: ${plural(top.waiting, "titular aguardando", "titulares aguardando")}${
        top.oldestWaitingDays !== null ? ` · mais antiga há ${plural(top.oldestWaitingDays, "dia", "dias")}` : ""
      }`;

  return (
    <Card variant="outlined">
      <CardActionArea
        disabled={!campaignWorkspaceActivityId}
        aria-label={text}
        onClick={() => history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/app_campaign/${campaignWorkspaceActivityId}?tipo=${TYPE}`)}
      >
        <CardContent>
          <Typography variant="subtitle1">{text}</Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
